import Refresh from "@mui/icons-material/Refresh"
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Feature } from "shared/feature"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { selectMeIsReady } from "src/features/me/meSlice"
import { useGetFeaturesQuery } from "src/features/things/thingsSaApi"
import FeatureForm from "./FeatureForm"
import SaFeatureCard from "./SaFeatureCard"
import SaLayout from "./SaLayout"
import { Sl2Btn } from "./Sl2Btn"

const pageSize = 100

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const WarningInfo: FC<{ feature: Feature }> = ({ feature }) => {
  return (
    <Stack direction="column" spacing={1} alignItems="center">
      <Typography variant="body2" fontWeight="bold" noWrap>
        Missing configuration
      </Typography>
    </Stack>
  )
}

const defaultColWidth = 230

export const SaFeaturesPage: FC = () => {
  const { t } = useTranslation()

  const isReady = useAppSelector(selectMeIsReady)

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [searchInput, setSearchInput] = useState("")

  const dispatch = useAppDispatch()

  // Fetch features using Super Admin access pattern SaGetFeatures
  const q = useGetFeaturesQuery(
    {
      params: {
        limit: pageSize,
        // Potential future filtering based on searchInput
        ...(searchInput ? { search: searchInput } : {}),
      },
    },
    { skip: !isReady },
  )

  // Prepare grouped rows of 3 features each for layout (row stacks of 3 column stacks)
  const featuresList =
    q.data?.things
      ?.slice()
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "")) ||
    []

  const featureRows: any[][] = []
  for (let i = 0; i < featuresList.length; i += 3) {
    featureRows.push(featuresList.slice(i, i + 3))
  }

  return (
    <SaLayout selectedMenuItemLabelKey="features">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("features")}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                {featuresList.length}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {q.isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={q.refetch}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <Sl2Btn
              variant="contained"
              disableElevation
              onClick={() => {
                setIsDialogOpen(true)
              }}
            >
              {t("createFeature")}
            </Sl2Btn>
          </Stack>
        </Stack>

        <Dialog data-ui="UI-SL2W-271" open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <DialogTitle>{t("createFeature")}</DialogTitle>
          <DialogContent>
            <FeatureForm
              onSuccess={() => {
                setIsDialogOpen(false)
              }}
              onCancel={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Intro / description card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack direction="column" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("feature.moduleDescription", {
                defaultValue:
                  "Features allow you to remotely enable or disable functionality for tenants or the entire platform without redeploying code.",
              })}
            </Typography>
            <Alert severity="info">
              <Typography variant="body2">
                {t("feature.featureFlaggingIntroBody", {
                  defaultValue:
                    "A feature flag (toggle) lets you gate behavior behind a dynamic switch. Use them to roll out gradually, run A/B tests, or disable problematic logic instantly.",
                })}
              </Typography>
            </Alert>
          </Stack>
        </Box>

        {/* Features list card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
          <Stack direction="column" spacing={3}>
            {q.isFetching && !q.data && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography variant="body2">{t("processing")}</Typography>
              </Stack>
            )}
            {!q.isLoading && (q.data?.things?.length || 0) === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("noData")}
              </Typography>
            )}
            <Stack direction="column" spacing={2}>
              {featureRows.map((row, rowIdx) => (
                <Stack
                  key={rowIdx}
                  direction="row"
                  spacing={2}
                  // alignItems="stretch"
                >
                  {row.map((f) => (
                    <Stack
                      key={f.id}
                      direction="column"
                      // flex={1}
                      minWidth={0}
                      sx={{ "& > *": { height: "100%" } }}
                    >
                      <SaFeatureCard feature={f} />
                    </Stack>
                  ))}
                  {row.length < 3 &&
                    Array.from({ length: 3 - row.length }).map((_, i) => (
                      <Box key={`placeholder-${i}`} flex={1} />
                    ))}
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </SaLayout>
  )
}

export default SaFeaturesPage
